import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { API } from "../lib/appCommon";
import { useAuth } from "../context/AuthContext";
import { FloorLanguageControl } from "./FloorLanguageControl";
import LastLoginBadge from "./LastLoginBadge";
import { EmailAlertsRow, EnableBrowserNotifButton } from "./NotifRows";
import AlertsCard from "./AlertsCard";
import OmegaP0DispatchToggle from "./OmegaP0DispatchToggle";
import LiveScannerFeed from "./LiveScannerFeed";
import AutopilotDecisionReplay from "./AutopilotDecisionReplay";
import OmegaBrainCard from "./OmegaBrainCard";
import OrderQueueCard from "./OrderQueueCard";
import OmegaBrainStatusCard from "./OmegaBrainStatusCard";
import IntegrationsStatusCard from "./IntegrationsStatusCard";
import LlmManagerCard from "./LlmManagerCard";
import TeaControlCenter from "./TeaControlCenter";
import OmegaSettingsPanel from "./OmegaSettingsPanel";
import { KnownLimitsCard, GrowthArcCard, GlossaryCard } from "./OmegaCanon";
import {
  QuestionEngineCard, SensingOverviewCard, SensingTriaxisCard, SectorIntelCard,
  StrategySelectorCard, BestExpressionCard, CalibrationCard, WorkedExampleCard,
} from "./OmegaCanonExt";
import "./SettingsTab.css";

// ─── Icons ──────────────────────────────────────────────────────────────────
const ICON_PATHS = {
  sliders: <><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></>,
  cpu: <><rect x="6" y="6" width="12" height="12" rx="2"/><path d="M9 2v3M15 2v3M9 19v3M15 19v3M2 9h3M2 15h3M19 9h3M19 15h3"/></>,
  bell: <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></>,
  book: <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></>,
  server: <><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></>,
};
function Icon({ name }) {
  const p = ICON_PATHS[name];
  if (!p) return null;
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{p}</svg>;
}

const SECTION_ACCENT = {
  preferences: { accent: "var(--blue)",   bg: "rgba(96,165,250,0.13)" },
  engine:      { accent: "var(--purple)", bg: "rgba(167,139,250,0.13)" },
  alerts:      { accent: "#f87171",       bg: "rgba(248,113,113,0.13)" },
  canon:       { accent: "var(--st-gold)", bg: "var(--st-gold-bg)" },
  system:      { accent: "var(--st-teal)", bg: "var(--st-teal-bg)" },
};

function Section({ id, activeId, title, icon, sub, children }) {
  if (id !== activeId) return null;
  return (
    <div className="st-section" style={{ "--accent": SECTION_ACCENT[id].accent, "--accent-bg": SECTION_ACCENT[id].bg }} data-testid={`st-section-${id}`}>
      <div className="st-section-head">
        <div>
          <div className="st-section-title"><Icon name={icon} />{title}</div>
          {sub && <div className="st-section-sub">{sub}</div>}
        </div>
      </div>
      <div className="st-section-body">{children}</div>
    </div>
  );
}

function SubToggle({ views, active, onChange }) {
  return (
    <div className="st-subtoggle">
      {views.map(([id, label]) => (
        <button key={id} className={active === id ? "on" : ""} onClick={() => onChange(id)}>{label}</button>
      ))}
    </div>
  );
}

function Switch({ on, onClick, testId }) {
  return (
    <button className={`st-switch ${on ? "on" : ""}`} onClick={onClick} data-testid={testId} aria-pressed={!!on}>
      <span className="knob" />
    </button>
  );
}

// ─── Brokerage account switcher ─────────────────────────────────────────────
function AccountSwitcher() {
  const [accounts, setAccounts] = useState([]);
  const [active, setActive] = useState("");
  const [switching, setSwitching] = useState(false);
  const [switchErr, setSwitchErr] = useState("");
  const [loadErr, setLoadErr] = useState(false);

  useEffect(() => {
    Promise.allSettled([
      axios.get(`${API}/accounts`),
      axios.get(`${API}/settings`),
    ]).then(([aRes, sRes]) => {
      if (aRes.status === "fulfilled") setAccounts(aRes.value.data?.accounts || []);
      else setLoadErr(true);
      if (sRes.status === "fulfilled") setActive(sRes.value.data?.activeAccountId || "");
    });
  }, []);

  const switchAccount = async (id) => {
    if (id === active || switching) return;
    setSwitching(true);
    setSwitchErr("");
    const prev = active;
    setActive(id);
    try {
      await axios.post(`${API}/accounts/switch`, { accountId: id });
    } catch (e) {
      setActive(prev);
      setSwitchErr("Switch failed — check connection and try again.");
    } finally { setSwitching(false); }
  };

  return (
    <div className="card" data-testid="account-switcher">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div className="sh" style={{ margin: 0 }}>Brokerage Accounts</div>
        {accounts.length > 0 && <span style={{ fontSize: 10, color: "var(--muted)" }}>{accounts.length} account{accounts.length !== 1 ? "s" : ""}</span>}
      </div>
      {loadErr ? (
        <div style={{ textAlign: "center", padding: 16, color: "#f87171", fontSize: 12 }}>Could not load accounts — check brokerage connection.</div>
      ) : accounts.length === 0 ? (
        <div style={{ textAlign: "center", padding: 16, color: "var(--dim)", fontSize: 12 }}>No brokerage accounts found. Ensure Public.com is connected.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8 }}>
          {accounts.map(a => (
            <div key={a.accountId} onClick={() => switchAccount(a.accountId)}
              style={{ padding: 12, borderRadius: 8, cursor: a.accountId === active ? "default" : "pointer", transition: "all 0.15s",
                background: a.accountId === active ? "rgba(59,130,246,0.08)" : "var(--layer2)",
                border: `1.5px solid ${a.accountId === active ? "#3b82f6" : "var(--border)"}`,
                opacity: switching && a.accountId !== active ? 0.5 : 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: a.accountId === active ? "#60a5fa" : "var(--text)" }}>
                {(a.accountType || "Account").replace("AccountType.", "").replace(/_/g, " ")}
              </div>
              <div style={{ fontSize: 10, color: "var(--dim)", fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>
                ···{a.accountId?.slice(-6) || ""}
              </div>
              {a.accountId === active && <span className="tag t-blue" style={{ fontSize: 8, marginTop: 4, display: "inline-block" }}>ACTIVE</span>}
            </div>
          ))}
        </div>
      )}
      {switching && <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 8 }}><span className="spin" /> Switching account...</div>}
      {switchErr && <div style={{ fontSize: 11, color: "#f87171", marginTop: 8 }}>{switchErr}</div>}
    </div>
  );
}

// ─── Preferences (General + Trading Config) ─────────────────────────────────
function PreferencesSection({
  view, setView, theme, setTheme, mobileUiMode, setMobileUiMode, navigate,
  settings, setSettings, saveSetting, saveNotif, profile, setProfile, saveProfile, saving,
  pwForm, setPwForm, changePassword, pwMsg, user,
}) {
  return (
    <div>
      <SubToggle views={[["general", "General"], ["trading", "Trading Config"]]} active={view} onChange={setView} />

      {view === "general" && (
        <>
          <div className="card">
            <div className="sh">Theme</div>
            <div className="st-tile-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
              {[
                { id: "dark", label: "Dark", desc: "Always dark mode", icon: "🌙" },
                { id: "light", label: "Light", desc: "Always light mode", icon: "☀️" },
                { id: "auto", label: "Auto (Market)", desc: "Light when market open, dark after close", icon: "🔄" },
              ].map(t => (
                <div key={t.id} className={`st-tile ${theme === t.id ? "on" : ""}`} onClick={() => setTheme(t.id)}>
                  <div className="icon">{t.icon}</div>
                  <div className="label">{t.label}</div>
                  <div className="desc">{t.desc}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 10, color: "var(--dim)", marginTop: 8 }}>Auto mode: Light theme 9:30 AM - 4:00 PM ET (market hours), Dark theme after close and weekends.</div>
          </div>

          <div className="card">
            <div className="sh">UI Mode</div>
            <div className="st-tile-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
              {[
                { id: "swipe",    label: "Swipe Cards", desc: "Classic card grid + peek",         icon: "📱",  color: "#a78bfa" },
                { id: "swipe2",   label: "Mobile 2.0",  desc: "Clean Robinhood-style UI",          icon: "✦",   color: "#00C805" },
                { id: "swipe3",   label: "Trading Floor", desc: "Live trading-floor glass deck",    icon: "🛰️",  color: "#0A84FF" },
                { id: "glass",    label: "UI Glass",     desc: "Trading Floor glass 2.0 preview",   icon: "◈",   color: "#38bdf8" },
                { id: "diamond",  label: "Diamond",      desc: "Next-gen UI — in progress",         icon: "◆",   color: "#5b8cff" },
                { id: "advanced", label: "Advanced",    desc: "Full desktop view on mobile",       icon: "🖥️",  color: "#3b82f6" },
              ].map(m => (
                <div key={m.id} data-testid={`mobile-ui-${m.id}`} className={`st-tile ${mobileUiMode === m.id ? "on" : ""}`}
                  style={mobileUiMode === m.id ? { borderColor: m.color, background: `${m.color}18` } : {}}
                  onClick={() => {
                    setMobileUiMode(m.id);
                    if (m.id === "swipe" || m.id === "swipe2" || m.id === "swipe3" || m.id === "glass" || m.id === "diamond") {
                      navigate("/mobile", { replace: true });
                    }
                  }}>
                  <div className="icon">{m.icon}</div>
                  <div className="label" style={mobileUiMode === m.id ? { color: m.color } : {}}>{m.label}</div>
                  <div className="desc">{m.desc}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 10, color: "var(--dim)", marginTop: 8 }}>Saved to your browser. On mobile: Swipe / Mobile 2.0 open the card interface, Trading Floor and UI Glass open the live glass floor deck, Diamond opens the new build in progress, and Advanced shows the full desktop dashboard.</div>
          </div>

          <div className="card" data-testid="floor-language-card"><FloorLanguageControl /></div>

          <div className="card" data-testid="public-showcase-card">
            <div className="sh">Public Showcase</div>
            <div className="srow">
              <div>
                <div className="slbl">Feature my anonymized weekly stats</div>
                <div className="ssub">Show this account on the landing page personas carousel as social proof. Only weekly P&L %, win rate, trades count, and equity sparkline are shared — no PII, no usernames, no individual ticker positions outside the top trade.</div>
              </div>
              <Switch on={!!settings?.featuredInPersonas} onClick={() => saveSetting("featuredInPersonas", !settings?.featuredInPersonas)} testId="featured-personas-toggle" />
            </div>
            {settings?.featuredInPersonas && (<>
              <div className="srow">
                <div><div className="slbl">Display Alias</div><div className="ssub">Anonymized name shown on the carousel (e.g. "Alex M." or "Chicago Trader"). Leave blank for "Omega trader · live".</div></div>
                <input className="inp" data-testid="persona-alias-input" maxLength={32} placeholder="e.g. Alex M." value={settings?.personaAlias || ""} onChange={e => saveSetting("personaAlias", e.target.value)} style={{ width: 200 }} />
              </div>
              <div className="srow">
                <div><div className="slbl">Starting Equity</div><div className="ssub">Used to compute weekly P&L %. Set this to your account size (e.g. 10000). Default $10,000 if blank.</div></div>
                <input className="inp" data-testid="persona-start-equity" type="number" min="100" step="100" value={settings?.startEquity || ""} onChange={e => saveSetting("startEquity", Number(e.target.value) || 0)} style={{ width: 120 }} />
              </div>
              <div style={{ fontSize: 11, color: "var(--green)", marginTop: 6, padding: "8px 10px", borderRadius: 8, background: "rgba(74,222,128,0.08)", border: "1px solid rgba(74,222,128,0.18)" }}>
                ✓ Live persona is enabled. Your anonymized weekly card will be the first tile in the rotation.
              </div>
            </>)}
          </div>

          <div className="card">
            <div className="sh">Account Profile</div>
            <div className="grid2" style={{ marginBottom: 10 }}>
              <div><div className="m-lbl">First Name</div><input className="inp" value={profile.firstName || ""} onChange={e => setProfile(p => ({ ...p, firstName: e.target.value }))} /></div>
              <div><div className="m-lbl">Last Name</div><input className="inp" value={profile.lastName || ""} onChange={e => setProfile(p => ({ ...p, lastName: e.target.value }))} /></div>
            </div>
            <div className="grid2" style={{ marginBottom: 10 }}>
              <div><div className="m-lbl">Email</div><input className="inp" value={profile.email || ""} onChange={e => setProfile(p => ({ ...p, email: e.target.value }))} /></div>
              <div><div className="m-lbl">Phone</div><input className="inp" value={profile.phone || ""} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} /></div>
            </div>
            <div style={{ marginBottom: 10 }}><div className="m-lbl">Timezone</div>
              <select className="sel" value={profile.timezone || ""} onChange={e => setProfile(p => ({ ...p, timezone: e.target.value }))}>
                <option value="">Select timezone</option>
                {["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Phoenix", "Pacific/Honolulu", "UTC"].map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
            <button className="st-btn primary" onClick={saveProfile} disabled={saving}>{saving ? "Saving..." : "Save Profile"}</button>
            <div style={{ fontSize: 10, color: "var(--dim)", marginTop: 6, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span>Username: {user?.username} | User ID: {user?.user_id}</span>
              <LastLoginBadge compact />
            </div>
          </div>

          <div className="card">
            <div className="sh">Change Password</div>
            <div className="grid3" style={{ marginBottom: 10 }}>
              <div><div className="m-lbl">Current Password</div><input className="inp" type="password" value={pwForm.current} onChange={e => setPwForm(p => ({ ...p, current: e.target.value }))} /></div>
              <div><div className="m-lbl">New Password</div><input className="inp" type="password" value={pwForm.new} onChange={e => setPwForm(p => ({ ...p, new: e.target.value }))} /></div>
              <div><div className="m-lbl">Confirm</div><input className="inp" type="password" value={pwForm.confirm} onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))} /></div>
            </div>
            <button className="st-btn ghost" onClick={changePassword}>Update Password</button>
            {pwMsg && <span style={{ fontSize: 11, color: pwMsg === "Password updated" ? "var(--green)" : "var(--red)", marginLeft: 10 }}>{pwMsg}</span>}
          </div>
        </>
      )}

      {view === "trading" && (
        <>
          <div className="card">
            <div className="sh">Trading Configuration</div>
            <div className="srow">
              <div>
                <div className="slbl">Auto-Accept Trades</div>
                <div className="ssub">{settings?.autopilotMode === "FULL_AUTO" ? "On — approved trades execute automatically" : "Off — Omega scans and alerts, but never executes on its own"}</div>
              </div>
              <Switch on={settings?.autopilotMode === "FULL_AUTO"} onClick={() => saveSetting("autopilotMode", settings?.autopilotMode === "FULL_AUTO" ? "ASSISTED" : "FULL_AUTO")} testId="setting-auto-accept" />
            </div>
            <div className="srow"><div><div className="slbl">Autopilot Mode</div><div className="ssub">Default automation level</div></div>
              <select className="sel" value={settings?.autopilotMode || "OBSERVE_ONLY"} onChange={e => saveSetting("autopilotMode", e.target.value)}>
                <option value="OFF">Off</option>
                <option value="OBSERVE_ONLY">Observe</option>
                <option value="ASSISTED">Assisted</option>
                <option value="GEM_ONLY">Gem Only</option>
                <option value="DEFENSE">Defense</option>
                <option value="FULL_AUTO">Full Auto</option>
                <option value="LIQUIDATE_RISK">Liquidate</option>
              </select>
            </div>
            <div className="srow"><div><div className="slbl">Zi Score Threshold</div><div className="ssub">Minimum score for trade signals</div></div>
              <input className="inp" type="number" value={settings?.ziThreshold || 60} onChange={e => saveSetting("ziThreshold", Number(e.target.value))} style={{ width: 70 }} />
            </div>
            <div className="srow"><div><div className="slbl">Max Daily Loss ($)</div><div className="ssub">Tightens your capital tier's daily-loss kill switch — can only lower it, never raise it above the tier's own cap</div></div>
              <input className="inp" type="number" value={settings?.maxDailyLoss || 500} onChange={e => saveSetting("maxDailyLoss", Number(e.target.value))} style={{ width: 80 }} />
            </div>
            <div className="srow"><div><div className="slbl">Max Drawdown %</div><div className="ssub">Tightens the drawdown kill switch — can only lower your tier's cap, never raise it</div></div>
              <input className="inp" type="number" value={settings?.maxDrawdownPct || 10} onChange={e => saveSetting("maxDrawdownPct", Number(e.target.value))} style={{ width: 70 }} />
            </div>
            <div className="srow"><div><div className="slbl">Max Concurrent Positions</div><div className="ssub">Tightens your tier's position-count ceiling — can only lower it, never raise it</div></div>
              <input className="inp" type="number" value={settings?.maxPositions || 5} onChange={e => saveSetting("maxPositions", Number(e.target.value))} style={{ width: 60 }} />
            </div>
            <div className="srow"><div><div className="slbl">Kelly Fraction</div><div className="ssub">Tightens position sizing — can only shrink your tier's Kelly multiplier, never grow it</div></div>
              <input className="inp" type="number" value={settings?.kellyFraction || 0.25} onChange={e => saveSetting("kellyFraction", Number(e.target.value))} style={{ width: 70 }} step="0.05" />
            </div>
            <div className="srow"><div><div className="slbl">Monte Carlo Paths</div><div className="ssub">Live scans clamp this to 5,000 max to keep scan-cycle cost bounded</div></div>
              <select className="sel" value={settings?.mcPaths || 10000} onChange={e => saveSetting("mcPaths", Number(e.target.value))}>
                <option value={5000}>5,000</option><option value={10000}>10,000</option>
                <option value={50000}>50,000</option><option value={100000}>100,000</option>
              </select>
            </div>
            <div className="srow"><div><div className="slbl">Trade Strategy</div><div className="ssub">Day Trade (same-day exit) or Swing (multi-day hold)</div></div>
              <select className="sel" value={settings?.tradeStrategy || "swing"} onChange={e => saveSetting("tradeStrategy", e.target.value)}>
                <option value="swing">Swing Trade (2-14 days)</option>
                <option value="day_trade">Day Trade (same-day exit)</option>
              </select>
            </div>
            <div className="srow"><div><div className="slbl">Account Type</div><div className="ssub">Cash: unlimited day trades (settled funds) | Margin: PDT rule applies under $25K</div></div>
              <select className="sel" value={settings?.accountType || "margin"} onChange={e => saveSetting("accountType", e.target.value)}>
                <option value="margin">Margin Account</option>
                <option value="cash">Cash Account</option>
              </select>
            </div>
          </div>

          <div className="card" style={{ borderColor: "rgba(251,191,36,0.2)" }}>
            <div className="sh" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              Trail Stop Automation
              <span className="tag" style={{ fontSize: 8, background: "rgba(251,191,36,0.15)", color: "#fbbf24", border: "1px solid rgba(251,191,36,0.3)" }}>AUTO-RISK</span>
            </div>
            <div className="srow">
              <div><div className="slbl">Default Trail %</div><div className="ssub">Used when you tap "Trail" on a position</div></div>
              <select className="sel" data-testid="setting-default-trail-pct" value={settings?.defaultTrailPct != null ? String(settings.defaultTrailPct) : "0.25"} onChange={e => saveSetting("defaultTrailPct", Number(e.target.value))}>
                <option value="0.1">10% · Tight</option>
                <option value="0.15">15%</option>
                <option value="0.2">20%</option>
                <option value="0.25">25% · Classic</option>
                <option value="0.3">30%</option>
                <option value="0.4">40% · Loose</option>
              </select>
            </div>
            <div className="srow">
              <div><div className="slbl">Auto-Arm Trail on Profit</div><div className="ssub">Automatically arm a trail stop when a position hits the profit threshold — gains are never un-protected</div></div>
              <Switch on={!!settings?.autoArmTrailOnProfit} onClick={() => saveSetting("autoArmTrailOnProfit", !settings?.autoArmTrailOnProfit)} testId="setting-auto-arm-trail" />
            </div>
            {settings?.autoArmTrailOnProfit && (
              <div className="srow">
                <div><div className="slbl">Auto-Arm Profit Threshold</div><div className="ssub">Unrealized P/L at which auto-arm kicks in</div></div>
                <select className="sel" data-testid="setting-auto-arm-threshold" value={settings?.autoArmProfitThreshold != null ? String(settings.autoArmProfitThreshold) : "0.15"} onChange={e => saveSetting("autoArmProfitThreshold", Number(e.target.value))}>
                  <option value="0.05">+5%</option>
                  <option value="0.1">+10%</option>
                  <option value="0.15">+15%</option>
                  <option value="0.2">+20%</option>
                  <option value="0.3">+30%</option>
                  <option value="0.5">+50%</option>
                </select>
              </div>
            )}
          </div>

          <div className="card">
            <div className="sh">Watchlist</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
              {(settings?.watchlist || []).map(s => (
                <span key={s} style={{ display: "flex", alignItems: "center", gap: 4, padding: "3px 8px", background: "var(--blue-bg)", border: "0.5px solid rgba(59,130,246,0.3)", borderRadius: 6, fontSize: 11, color: "var(--blue)" }}>
                  {s}
                  <span style={{ cursor: "pointer", color: "var(--red)", fontWeight: 700, marginLeft: 2 }}
                    onClick={async () => { try { const r = await axios.post(`${API}/watchlist/remove`, { symbol: s }); setSettings(prev => ({ ...prev, watchlist: r.data?.watchlist || prev.watchlist.filter(x => x !== s) })); } catch {} }}>
                    &times;
                  </span>
                </span>
              ))}
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const inp = e.target.elements.wlInput;
              const sym = inp.value.trim().toUpperCase();
              if (!sym) return;
              try { const r = await axios.post(`${API}/watchlist/add`, { symbol: sym }); setSettings(prev => ({ ...prev, watchlist: r.data?.watchlist || [...(prev?.watchlist || []), sym] })); inp.value = ""; } catch {}
            }} style={{ display: "flex", gap: 6 }}>
              <input className="inp" name="wlInput" placeholder="Add ticker..." style={{ width: 100 }} data-testid="watchlist-input" />
              <button type="submit" className="st-btn primary" style={{ fontSize: 10 }} data-testid="watchlist-add">Add</button>
            </form>
          </div>

          <div className="card">
            <div className="sh">Notification Preferences</div>
            <EnableBrowserNotifButton />
            {[
              { key: "tradeExecuted", label: "Trade executed (Full Auto)", sub: "Notify on every automated fill" },
              { key: "ziAlert", label: "Zi Score alert (Semi-Auto)", sub: "Notify when ticker crosses threshold" },
              { key: "riskGuard", label: "Risk guard triggered", sub: "Alert when risk gates activate" },
              { key: "regimeChange", label: "Regime change detected", sub: "HMM state transition alert" },
            ].map(n => (
              <div key={n.key} className="srow">
                <div><div className="slbl">{n.label}</div><div className="ssub">{n.sub}</div></div>
                <input type="checkbox" checked={!!(settings?.notifications?.[n.key] ?? true)} onChange={e => saveNotif(n.key, e.target.checked)} />
              </div>
            ))}
            <EmailAlertsRow settings={settings} saveNotif={saveNotif} />
          </div>
        </>
      )}
    </div>
  );
}

// ─── Omega Engine (Automation Thinking + Omega AI) ──────────────────────────
function EngineSection({ view, setView, paperStats, thinking }) {
  return (
    <div>
      <SubToggle views={[["feed", "Live Feed"], ["health", "Health & Personality"]]} active={view} onChange={setView} />

      {view === "feed" && (
        <>
          <div className="card" style={{ borderColor: "rgba(167,139,250,0.3)", background: "rgba(167,139,250,0.03)" }}>
            <div className="row">
              <div className="sh" style={{ display: "flex", alignItems: "center", gap: 6 }}>Omega ZI AI — Unified Command Feed <span className="tag t-pur" style={{ fontSize: 8 }}>SSE Live</span></div>
            </div>
            <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 10 }}>Every move, every thought, every execution, every gate check — streamed in real-time via SSE.</div>
            {paperStats && (
              <div style={{ marginBottom: 12, padding: 10, background: "rgba(96,165,250,0.04)", borderRadius: 8, display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: "var(--blue)" }}>Background Learning (z$)</span>
                <span style={{ fontSize: 10, color: "var(--muted)" }}>Status: <span style={{ color: paperStats.status === "learning" ? "var(--green)" : "var(--yellow)" }}>{paperStats.status}</span></span>
                <span style={{ fontSize: 10, color: "var(--muted)" }}>Paper Trades: {paperStats.totalPaperTrades} ({paperStats.openPaper} open)</span>
                <span style={{ fontSize: 10, color: "var(--muted)" }}>Win Rate: <span style={{ color: paperStats.paperWinRate >= 60 ? "var(--green)" : "var(--yellow)" }}>{paperStats.paperWinRate}%</span></span>
              </div>
            )}
            <LiveScannerFeed />
          </div>
          <AutopilotDecisionReplay />
          <OmegaBrainCard />
          <OrderQueueCard />
        </>
      )}

      {view === "health" && (
        <>
          <OmegaBrainStatusCard />
          <IntegrationsStatusCard />
          <TeaControlCenter />
          <OmegaSettingsPanel />
        </>
      )}
    </div>
  );
}

// ─── Settings Tab (root) ────────────────────────────────────────────────────
export default function SettingsTab({ health, theme, setTheme, mobileUiMode, setMobileUiMode }) {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [activeId, setActiveId] = useState("preferences");
  const [prefView, setPrefView] = useState("general");
  const [engineView, setEngineView] = useState("feed");

  const [settings, setSettings] = useState(null);
  const [profile, setProfile] = useState({});
  const [saving, setSaving] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", new: "", confirm: "" });
  const [pwMsg, setPwMsg] = useState("");
  const [thinking, setThinking] = useState([]);
  const [paperStats, setPaperStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings`).then(r => setSettings(r.data)).catch(() => {});
    if (user) setProfile({ firstName: user.firstName || "", lastName: user.lastName || "", email: user.email || "", phone: user.phone || "", timezone: user.timezone || "" });
  }, [user]);

  useEffect(() => {
    if (engineView !== "feed" || activeId !== "engine") return;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/autopilot/thinking?limit=100`);
        setThinking(Array.isArray(r.data) ? r.data : []);
      } catch {}
      axios.get(`${API}/autopilot/paper-stats`).then(r => setPaperStats(r.data)).catch(() => {});
    };
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [engineView, activeId]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/user/profile`, profile);
      setUser(r.data);
      if (profile.email) {
        try {
          await axios.put(`${API}/settings`, { email: profile.email });
          setSettings(prev => ({ ...(prev || {}), email: profile.email }));
        } catch {}
      }
      toast.success("Profile saved");
    } catch {
      toast.error("Failed to save profile — try again.");
    } finally { setSaving(false); }
  };

  const saveSetting = async (key, val) => {
    const updated = { ...settings, [key]: val };
    setSettings(updated);
    try { await axios.put(`${API}/settings`, { [key]: val }); } catch {
      toast.error("Setting didn't save — try again.");
    }
  };

  const saveNotif = async (key, val) => {
    const notifs = { ...(settings?.notifications || {}), [key]: val };
    setSettings(prev => ({ ...prev, notifications: notifs }));
    try { await axios.put(`${API}/settings`, { notifications: notifs }); } catch {
      toast.error("Notification setting didn't save — try again.");
    }
  };

  const changePassword = async () => {
    if (pwForm.new !== pwForm.confirm) { setPwMsg("Passwords don't match"); return; }
    try {
      await axios.post(`${API}/user/password`, { currentPassword: pwForm.current, newPassword: pwForm.new });
      setPwMsg("Password updated");
      setPwForm({ current: "", new: "", confirm: "" });
    } catch (e) { setPwMsg(e.response?.data?.detail || "Failed"); }
  };

  const SECTIONS = [
    { id: "preferences", label: "Preferences", icon: "sliders" },
    { id: "engine",       label: "Omega Engine", icon: "cpu" },
    { id: "alerts",       label: "Alerts",       icon: "bell" },
    { id: "canon",        label: "Canon",        icon: "book" },
    { id: "system",       label: "System",       icon: "server" },
  ];
  const rootAccent = SECTION_ACCENT[activeId] || SECTION_ACCENT.preferences;

  return (
    <div className="st-root" style={{ "--accent": rootAccent.accent, "--accent-bg": rootAccent.bg }} data-testid="settings-tab">
      <div className="st-banner">
        <div>
          <div className="st-banner-title">
            <Icon name="sliders" />
            <span>Settings</span>
            <span className="tag t-bull" style={{ fontSize: 8 }}>Live</span>
          </div>
          <div className="st-banner-sub">Preferences, the Omega automation engine, alerts, and the capability canon.</div>
        </div>
      </div>

      <div className="st-jump-strip">
        {SECTIONS.map(s => (
          <button key={s.id} className={activeId === s.id ? "on" : ""} onClick={() => setActiveId(s.id)} data-testid={`st-jump-${s.id}`}>
            <Icon name={s.icon} />{s.label}
          </button>
        ))}
      </div>

      <Section id="preferences" activeId={activeId} title="Preferences" icon="sliders" sub="Profile, theme, and trading configuration">
        <PreferencesSection
          view={prefView} setView={setPrefView} theme={theme} setTheme={setTheme}
          mobileUiMode={mobileUiMode} setMobileUiMode={setMobileUiMode} navigate={navigate}
          settings={settings} setSettings={setSettings} saveSetting={saveSetting} saveNotif={saveNotif}
          profile={profile} setProfile={setProfile} saveProfile={saveProfile} saving={saving}
          pwForm={pwForm} setPwForm={setPwForm} changePassword={changePassword} pwMsg={pwMsg} user={user}
        />
      </Section>

      <Section id="engine" activeId={activeId} title="Omega Engine" icon="cpu" sub="Live scanning feed and engine health / personality">
        <EngineSection view={engineView} setView={setEngineView} paperStats={paperStats} thinking={thinking} />
      </Section>

      <Section id="alerts" activeId={activeId} title="Alerts" icon="bell" sub="Web push, SMS, and P0-enhancement dispatch">
        <AlertsCard />
        <OmegaP0DispatchToggle />
      </Section>

      <Section id="canon" activeId={activeId} title="Canon" icon="book" sub="Omega Capability Canon — full reasoning + doctrine coverage">
        <KnownLimitsCard />
        <SensingOverviewCard />
        <QuestionEngineCard />
        <SensingTriaxisCard />
        <SectorIntelCard />
        <StrategySelectorCard />
        <BestExpressionCard />
        <WorkedExampleCard defaultSymbol="AAL" />
        <CalibrationCard />
        <GrowthArcCard />
        <GlossaryCard />
      </Section>

      <Section id="system" activeId={activeId} title="System" icon="server" sub="Brokerage accounts, data sources, and system status">
        <AccountSwitcher />
        <LlmManagerCard />
        <div className="card">
          <div className="sh">Data Sources</div>
          <div className="srow"><div><div className="slbl">Public.com API (Live)</div><div className="ssub">Quotes, options, Greeks, portfolio, orders</div></div><span className="tag t-bull">Active</span></div>
          <div className="srow"><div><div className="slbl">Yahoo Finance (OHLCV)</div><div className="ssub">Historical bars for charts + regime</div></div><span className="tag t-bull">Active</span></div>
          <div className="srow"><div><div className="slbl">Omega ZI AI Engine</div><div className="ssub">Portfolio chat, Tea Score, daily intel</div></div><span className="tag t-bull">Active</span></div>
          <div className="srow"><div><div className="slbl">Zi Engine v5.0</div><div className="ssub">BS, MC, Kelly, HMM, 10-stage pipeline</div></div><span className="tag t-bull">Active</span></div>
        </div>
        <div className="card">
          <div className="sh">System Status</div>
          <div className="srow"><div><div className="slbl">Data Source</div></div><span style={{ fontWeight: 500 }}>{health?.dataSource || "Public.com API"}</span></div>
          <div className="srow"><div><div className="slbl">Yahoo Cache</div></div><span style={{ fontWeight: 500 }}>{health?.cache?.live || 0} entries</span></div>
          <div className="srow"><div><div className="slbl">Flow Scanner</div></div><span style={{ fontWeight: 500 }}>{health?.flow?.tapeLength || 0} events / {health?.flow?.emaBases || 0} tracked</span></div>
        </div>
      </Section>
    </div>
  );
}
